import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { extractRegLinkSource } from "./utils/referralUtils";
import {
  coerceVisibleKeys,
  pagesOf,
  validateAnswers,
} from "./utils/surveyRuntime";

/**
 * PUBLIC SURVEY — the respondent form.
 *
 * Route: `/survey/:eventId/:slug`
 *
 * ⚠ TWO SEGMENTS, NOT ONE, for two independent reasons — both of which matter:
 *
 *  1. `/survey/:link` ALREADY EXISTS and belongs to the legacy registration-bound post-event survey.
 *     That path is live for real events; a one-segment route here would either shadow it or depend on a
 *     fallback chain, and breaking a live path to save a URL segment is not a trade worth making.
 *
 *  2. A SLUG ALONE CANNOT IDENTIFY A SURVEY. Slugs are unique per EVENT (`{eventId}_svy_forms.slug`),
 *     so two events may both own `delegate-experience-2026`. `/survey/<slug>` is therefore ambiguous by
 *     construction — the event id is required for correctness, not merely for routing convenience.
 *
 * Follows `CertificateVerifyPage.jsx` exactly: a public page that talks to RECEPTION's GraphQL with a
 * plain fetch, rather than to www's default api-reg Apollo client.
 *
 * ┌─ WHAT MAKES THIS DEPLOY ONCE ─────────────────────────────────────────────────────────────────┐
 * │ www is hand-uploaded with no CI, so nothing here may be survey-specific. Questions, pages,      │
 * │ theme, copy and closed states all arrive as DATA, and the theme arrives as already-resolved CSS │
 * │ custom properties — so even a brand-new palette needs no build. The only change that would ever │
 * │ require another upload is a brand-new question TYPE, which is why all sixteen ship here at once.│
 * └───────────────────────────────────────────────────────────────────────────────────────────────┘
 */

const RECEPTION_GRAPHQL =
  process.env.REACT_APP_RECEPTION_API_URL ||
  "https://api-reception.eventegration.com/graphql";

const SURVEY_QUERY = `
  query PublicSurvey($eventId: String!, $slug: String!, $ref: String) {
    publicSurvey(eventId: $eventId, slug: $slug, ref: $ref) {
      eventId slug title description publishedRevision
      isOpen closedReason closedHeadline closedBody opensAt
      layout numbering progress submitLabel multipleResponses thankYouHtml redirectUrl
      questions theme tokensLight tokensDark appearance preferredAppearance
    }
  }
`;

const SUBMIT_MUTATION = `
  mutation SubmitSurveyResponse($eventId: String!, $slug: String!, $answers: JSON!, $responseKey: String, $channel: String) {
    submitSurveyResponse(eventId: $eventId, slug: $slug, answers: $answers, responseKey: $responseKey, channel: $channel) {
      ok reason reference errors { key message }
    }
  }
`;

/** A stable per-visit id, so a retry replays rather than duplicating. */
function makeResponseKey() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) {
    /* fall through */
  }
  return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

function storageKey(slug, revision) {
  // Keyed on the PUBLISHED REVISION: a republished survey must never restore answers into a form whose
  // questions have changed under them.
  return `survey:${slug}:${revision}`;
}

async function callReception(query, variables) {
  const res = await fetch(RECEPTION_GRAPHQL, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json && json.errors && json.errors.length) {
    const message = json.errors[0] && json.errors[0].message;
    throw new Error(message || "Request failed");
  }
  return json && json.data ? json.data : null;
}

export default function PublicSurveyPage() {
  const { eventId, slug } = useParams();

  const [state, setState] = useState({ loading: true, survey: null, error: null });
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [resumeOffer, setResumeOffer] = useState(null);
  const [dark, setDark] = useState(false);
  const [countdown, setCountdown] = useState(null);

  const responseKey = useRef(makeResponseKey());
  const headingRef = useRef(null);
  const summaryRef = useRef(null);

  /* ── Load ─────────────────────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // The per-channel link stamp, read from the address by the SAME helper the registration pages
        // use — so survey sources and registration sources roll up through one classifier.
        let channel = null;
        try {
          channel = extractRegLinkSource();
        } catch (_) {
          channel = null;
        }
        const data = await callReception(SURVEY_QUERY, { eventId, slug, ref: channel });
        if (!alive) return;
        setState({ loading: false, survey: data ? data.publicSurvey : null, error: null });
      } catch (err) {
        if (!alive) return;
        setState({ loading: false, survey: null, error: err.message || "Something went wrong" });
      }
    })();
    return () => {
      alive = false;
    };
  }, [eventId, slug]);

  const survey = state.survey;

  /** Which appearance to paint. The client's fixed choice wins; `system` asks the device. */
  useEffect(() => {
    if (!survey) return;
    if (survey.appearance === "dark") return setDark(true);
    if (survey.appearance === "light") return setDark(false);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    setDark(mq.matches);
    const onChange = (e) => setDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
    };
  }, [survey]);

  /** Offer to resume, rather than silently restoring — a respondent must know what she is looking at. */
  useEffect(() => {
    if (!survey || !survey.isOpen) return;
    try {
      const raw = window.localStorage.getItem(storageKey(survey.slug, survey.publishedRevision));
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && saved.answers && Object.keys(saved.answers).length > 0) {
        setResumeOffer(saved);
      }
    } catch (_) {
      /* a blocked or full localStorage must never stop the form loading */
    }
  }, [survey]);

  /** Shadow every change, so a closed tab or a dead battery costs nothing. */
  useEffect(() => {
    if (!survey || Object.keys(answers).length === 0) return;
    try {
      window.localStorage.setItem(
        storageKey(survey.slug, survey.publishedRevision),
        JSON.stringify({ answers, at: new Date().toISOString(), page }),
      );
    } catch (_) {
      /* ignore: private mode, quota, or a browser blocking site data */
    }
  }, [answers, page, survey]);

  const pages = useMemo(() => (survey ? pagesOf(survey.questions, survey.layout) : []), [survey]);
  /**
   * Memoised, and not for tidiness: the `|| []` fallback produces a NEW array on every render, which gave
   * the `advance` callback below a fresh identity every render and defeated its own memo. Small, but this
   * is the callback that submits the form, and re-creating it on every keystroke is exactly the kind of
   * thing that turns into a double-submit later.
   */
  const currentPage = useMemo(
    () => pages[Math.min(page, Math.max(0, pages.length - 1))] || [],
    [pages, page],
  );
  const isLast = page >= pages.length - 1;

  const setAnswer = useCallback((key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    // Clear the error as soon as she engages with the field. Leaving it until the next submit means she
    // fixes something and is still told it is wrong.
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  /* ── Submit ───────────────────────────────────────────────────────────────────────────────────── */

  const advance = useCallback(async () => {
    if (!survey) return;
    const visible = coerceVisibleKeys(currentPage);
    const pageErrors = validateAnswers(survey.questions, answers, visible);
    if (Object.keys(pageErrors).length > 0) {
      setErrors(pageErrors);
      // Focus the SUMMARY, not the first bad field: it says how many problems there are, and each line
      // is a link to its field. Jumping straight to one field hides that there are three.
      requestAnimationFrame(() => summaryRef.current && summaryRef.current.focus());
      return;
    }

    if (!isLast) {
      setPage((p) => p + 1);
      setErrors({});
      // Moving focus to the new page heading is what makes a stepped form usable with a screen reader;
      // without it, focus stays on a button that has just been replaced.
      requestAnimationFrame(() => headingRef.current && headingRef.current.focus());
      return;
    }

    // Final submit — validate the WHOLE survey, not just this page.
    const allErrors = validateAnswers(survey.questions, answers);
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      setPage(0);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      let channel = null;
      try {
        channel = extractRegLinkSource();
      } catch (_) {
        channel = null;
      }
      const data = await callReception(SUBMIT_MUTATION, {
        eventId,
        slug,
        answers,
        responseKey: responseKey.current,
        channel,
      });
      const result = data && data.submitSurveyResponse;
      if (!result) throw new Error("No response from the server");

      if (!result.ok) {
        if (result.reason === "invalid" && result.errors && result.errors.length) {
          const mapped = {};
          result.errors.forEach((e) => {
            mapped[e.key] = e.message;
          });
          setErrors(mapped);
          setPage(0);
          setSubmitting(false);
          return;
        }
        // Closed while she was filling it in. Re-read so she sees the client's own closed card rather
        // than a bare error.
        setState((s) => ({ ...s, survey: { ...s.survey, isOpen: false, closedReason: result.reason } }));
        setSubmitting(false);
        return;
      }

      try {
        window.localStorage.removeItem(storageKey(survey.slug, survey.publishedRevision));
      } catch (_) {
        /* ignore */
      }
      setSubmitted({ reference: result.reference });
      if (survey.redirectUrl) setCountdown(5);
    } catch (err) {
      // Her answers are untouched and still on screen. That is the whole point of not clearing them.
      setSubmitError(err.message || "We couldn't send your answers.");
    } finally {
      setSubmitting(false);
    }
  }, [answers, currentPage, eventId, isLast, slug, survey]);

  /** A VISIBLE countdown, never an instant redirect — that would destroy the confirmation she needs. */
  useEffect(() => {
    if (countdown == null) return;
    if (countdown <= 0) {
      window.location.href = survey.redirectUrl;
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, survey]);

  /* ── Render ───────────────────────────────────────────────────────────────────────────────────── */

  const tokens = survey ? (dark ? survey.tokensDark : survey.tokensLight) : null;
  const rootStyle = useMemo(() => {
    const style = { minHeight: "100vh", boxSizing: "border-box" };
    if (tokens) Object.keys(tokens).forEach((k) => { style[k] = tokens[k]; });
    return style;
  }, [tokens]);

  if (state.loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f7f5fb", padding: "40px 20px", boxSizing: "border-box" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <Skeleton height={44} width={44} radius={12} />
          <div style={{ height: 16 }} />
          <Skeleton height={28} width="70%" />
          <div style={{ height: 10 }} />
          <Skeleton height={14} width="45%" />
          <div style={{ height: 28 }} />
          <Skeleton height={104} />
          <div style={{ height: 14 }} />
          <Skeleton height={104} />
        </div>
      </div>
    );
  }

  // A bad slug, an unpublished draft, a deleted survey and a disabled capability are ONE card. No event
  // name, no slug echoed back — anything more is an enumeration oracle on an open endpoint.
  if (!survey) {
    return (
      <SurveyShell style={{ minHeight: "100vh", background: "#f7f5fb", boxSizing: "border-box" }}>
        <TerminalCard tone="error" icon="?" title="This survey link isn't valid">
          Check the address, or ask whoever sent it to you for a fresh link.
        </TerminalCard>
      </SurveyShell>
    );
  }

  if (submitted) {
    return (
      <SurveyShell style={rootStyle}>
        <TerminalCard tone="ok" icon="✓" title="" html={survey.thankYouHtml}>
          {survey.multipleResponses && (
            <button
              type="button"
              style={btnGhost}
              onClick={() => {
                setSubmitted(null);
                setAnswers({});
                setErrors({});
                setPage(0);
                responseKey.current = makeResponseKey();
              }}
            >
              Submit another response
            </button>
          )}
          {countdown != null && (
            <div style={{ marginTop: 16, fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
              Taking you onward in <strong style={{ fontVariantNumeric: "tabular-nums" }}>{countdown}</strong> seconds…
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12, flexWrap: "wrap" }}>
                <button type="button" style={btnPrimary} onClick={() => setCountdown(0)}>Go now</button>
                <button type="button" style={btnGhost} onClick={() => setCountdown(null)}>Stay here</button>
              </div>
            </div>
          )}
          {submitted.reference && (
            <div style={{ marginTop: 16, fontFamily: "monospace", fontSize: 12, color: "hsl(var(--muted-foreground))", wordBreak: "break-all" }}>
              Reference {submitted.reference.slice(0, 13)}
            </div>
          )}
        </TerminalCard>
      </SurveyShell>
    );
  }

  if (!survey.isOpen) {
    const notYet = survey.closedReason === "not_open_yet";
    return (
      <SurveyShell style={rootStyle}>
        <TerminalCard
          tone={notYet ? "info" : "muted"}
          icon={notYet ? "🕑" : "🔒"}
          title={notYet ? "This survey opens soon" : survey.closedHeadline}
        >
          {notYet
            ? survey.opensAt
              ? `It opens on ${new Date(survey.opensAt).toLocaleString()}. The link will work from then.`
              : "The link will work once it opens."
            : survey.closedBody}
          {/* Logo and title stay visible so she knows she had the RIGHT link — it closed, she didn't mistype. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, marginTop: 20, opacity: 0.8, flexWrap: "wrap" }}>
            {survey.theme && survey.theme.logoUrl ? (
              <img src={survey.theme.logoUrl} alt="" style={{ width: 28, height: 28, borderRadius: 8, objectFit: "cover" }} />
            ) : null}
            <span style={{ fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>{survey.title}</span>
          </div>
        </TerminalCard>
      </SurveyShell>
    );
  }

  const answerableCount = survey.questions.filter((q) => !isBlock(q) && q.type !== "hidden").length;
  const answeredCount = survey.questions.filter((q) => {
    if (isBlock(q) || q.type === "hidden") return false;
    const v = answers[q.key];
    return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  }).length;

  const errorList = Object.keys(errors)
    .map((key) => {
      const q = survey.questions.find((x) => x.key === key);
      return q ? { key, label: q.label, message: errors[key] } : null;
    })
    .filter(Boolean);

  return (
    <SurveyShell style={rootStyle}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          advance();
        }}
        noValidate
      >
        {survey.theme && survey.theme.coverUrl ? (
          // In a band ABOVE the card, never behind the questions: text must never sit on an
          // uncontrolled photograph, whatever the client uploaded.
          <div
            style={{
              height: 104,
              width: "100%",
              backgroundImage: `url(${survey.theme.coverUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: "var(--survey-radius, 12px)",
              marginBottom: 16,
            }}
            role="presentation"
          />
        ) : null}

        {survey.theme && survey.theme.logoUrl ? (
          <img
            src={survey.theme.logoUrl}
            alt=""
            style={{ width: 46, height: 46, borderRadius: 11, objectFit: "cover", display: "block", marginBottom: 14, maxWidth: "100%" }}
          />
        ) : null}

        <h1
          ref={headingRef}
          tabIndex={-1}
          style={{
            margin: "0 0 6px",
            fontSize: 23,
            lineHeight: 1.25,
            fontWeight: 600,
            color: "hsl(var(--foreground))",
            outline: "none",
            overflowWrap: "anywhere",
          }}
        >
          {survey.title}
        </h1>
        {survey.description ? (
          <p style={{ margin: "0 0 20px", fontSize: 13.5, color: "hsl(var(--muted-foreground))", overflowWrap: "anywhere" }}>
            {survey.description}
          </p>
        ) : (
          <div style={{ height: 20 }} />
        )}

        {survey.progress && (
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
            <div style={{ flex: "1 1 auto", minWidth: 0, height: 6, borderRadius: 99, background: "hsl(var(--input))", overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  borderRadius: 99,
                  background: "hsl(var(--primary))",
                  transition: "width 150ms ease",
                  width:
                    pages.length > 1
                      ? `${Math.round(((page + 1) / pages.length) * 100)}%`
                      : `${answerableCount === 0 ? 0 : Math.round((answeredCount / answerableCount) * 100)}%`,
                }}
              />
            </div>
            {/* Never a fabricated percentage — both of these statements are literally true. */}
            <span style={{ flex: "0 0 auto", fontFamily: "monospace", fontSize: 11.5, fontVariantNumeric: "tabular-nums", color: "hsl(var(--muted-foreground))" }}>
              {pages.length > 1 ? `Step ${page + 1} of ${pages.length}` : `${answeredCount} of ${answerableCount} answered`}
            </span>
          </div>
        )}

        {resumeOffer && (
          <div style={{ ...cardStyle, borderColor: "hsl(var(--primary))", marginBottom: "var(--survey-gap, 16px)", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 19, lineHeight: 1 }} aria-hidden>↩</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <strong style={{ fontSize: 13.5, color: "hsl(var(--foreground))" }}>Continue where you left off?</strong>
              <p style={{ margin: "3px 0 10px", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
                You started this at{" "}
                <span style={{ fontFamily: "monospace" }}>
                  {new Date(resumeOffer.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>{" "}
                and answered {Object.keys(resumeOffer.answers).length}.
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  style={btnPrimary}
                  onClick={() => {
                    setAnswers(resumeOffer.answers);
                    setPage(Math.min(resumeOffer.page || 0, Math.max(0, pages.length - 1)));
                    setResumeOffer(null);
                  }}
                >
                  Continue
                </button>
                <button
                  type="button"
                  style={btnGhost}
                  onClick={() => {
                    try {
                      window.localStorage.removeItem(storageKey(survey.slug, survey.publishedRevision));
                    } catch (_) {
                      /* ignore */
                    }
                    setResumeOffer(null);
                  }}
                >
                  Start fresh
                </button>
              </div>
            </div>
          </div>
        )}

        {errorList.length > 0 && (
          <div
            ref={summaryRef}
            tabIndex={-1}
            role="alert"
            style={{
              background: "hsl(var(--destructive) / 0.1)",
              border: "1px solid hsl(var(--destructive) / 0.35)",
              borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
              padding: "13px 15px",
              marginBottom: "var(--survey-gap, 16px)",
              outline: "none",
            }}
          >
            <strong style={{ display: "block", fontSize: 13.5, color: "hsl(var(--destructive))", marginBottom: 7 }}>
              {errorList.length === 1 ? "1 answer needs attention" : `${errorList.length} answers need attention`}
            </strong>
            {errorList.map((e) => (
              <button
                key={e.key}
                type="button"
                onClick={() => {
                  const el = document.getElementById(`sv-${e.key}`);
                  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  if (el && el.focus) el.focus();
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  padding: "2px 0",
                  fontSize: 12.5,
                  color: "hsl(var(--destructive))",
                  textDecoration: "underline",
                  cursor: "pointer",
                  overflowWrap: "anywhere",
                }}
              >
                {e.label || "This question"} — {e.message}
              </button>
            ))}
          </div>
        )}

        {submitError && (
          <div
            role="alert"
            style={{
              background: "hsl(var(--destructive) / 0.1)",
              border: "1px solid hsl(var(--destructive) / 0.35)",
              borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
              padding: "13px 15px",
              marginBottom: "var(--survey-gap, 16px)",
            }}
          >
            <strong style={{ display: "block", fontSize: 13.5, color: "hsl(var(--destructive))" }}>
              We couldn&apos;t send your answers
            </strong>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>
              {submitError} <strong>Nothing you typed is lost</strong> — press Submit again.
            </p>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: "var(--survey-gap, 16px)" }}>
          {currentPage.map((question, index) => (
            <QuestionBlock
              key={question.id || question.key || index}
              question={question}
              value={answers[question.key]}
              error={errors[question.key]}
              number={survey.numbering ? numberOf(survey.questions, question) : null}
              onChange={(v) => setAnswer(question.key, v)}
            />
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
          {page > 0 && (
            <button type="button" style={btnGhost} onClick={() => { setPage((p) => p - 1); setErrors({}); }}>
              ← Back
            </button>
          )}
          <span style={{ flex: "1 1 auto" }} />
          {/*
            NEVER DISABLED while submitting is false. A disabled Next with no explanation is the classic
            dead end: the respondent cannot tell whether the form is broken or she is.
          */}
          <button type="submit" style={{ ...btnPrimary, minWidth: 140, opacity: submitting ? 0.7 : 1 }} disabled={submitting} aria-busy={submitting}>
            {submitting ? "Sending…" : isLast ? survey.submitLabel || "Submit" : "Next →"}
          </button>
        </div>

        <p style={{ marginTop: 22, textAlign: "center", fontSize: 11.5, color: "hsl(var(--muted-foreground))", overflowWrap: "anywhere" }}>
          {survey.theme && survey.theme.footerText ? `${survey.theme.footerText} · ` : ""}powered by Eventegration
        </p>
      </form>
    </SurveyShell>
  );
}

/* ── Shell and shared styles ─────────────────────────────────────────────────────────────────────── */

/**
 * `box-sizing: border-box` is set on the shell AND inherited by everything inside it.
 *
 * Not boilerplate: this page is mounted inside an app whose global CSS we do not control, and a
 * `content-box` input with `width: 100%` plus horizontal padding overflows its parent by exactly the
 * padding — the single most common cause of a form that scrolls sideways on a phone.
 */
function SurveyShell({ children, style }) {
  return (
    <div style={{ ...style, background: "hsl(var(--background, 240 20% 99%))", padding: "26px 16px 32px" }}>
      <style>{`
        .sv-root, .sv-root * { box-sizing: border-box; }
        .sv-root input, .sv-root textarea, .sv-root select, .sv-root button { font-family: inherit; font-size: inherit; }
        /* Long unbroken strings — a pasted URL in a label — must wrap rather than widen the page. */
        .sv-root { overflow-wrap: break-word; }
        .sv-root textarea { resize: vertical; }
        @media (prefers-reduced-motion: reduce) {
          .sv-root *, .sv-root *::before, .sv-root *::after { transition: none !important; animation: none !important; }
        }
      `}</style>
      <div
        className="sv-root"
        style={{
          // `min(100%, …)` is what stops the client's "Wide" choice from overflowing a narrow screen.
          maxWidth: "min(100%, var(--survey-width, 640px))",
          margin: "0 auto",
          color: "hsl(var(--foreground))",
          fontFamily: '"Outfit", system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * NOTE the fallbacks live INSIDE `var()`, never as a second argument to `hsl()`. `hsl(var(--x), #fff)` is
 * invalid CSS and the browser discards the whole declaration — which shows up as a transparent element
 * rather than as an error, so it is easy to ship.
 */
const cardStyle = {
  background: "hsl(var(--card, 0 0% 100%))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "var(--survey-radius, 12px)",
  padding: 16,
  color: "hsl(var(--card-foreground))",
};

const btnBase = {
  // 46px, so the target clears the 44px minimum with the border included.
  minHeight: 46,
  padding: "12px 22px",
  borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
  fontWeight: 600,
  fontSize: 14.5,
  cursor: "pointer",
  border: "1px solid transparent",
};

const btnPrimary = {
  ...btnBase,
  background: "hsl(var(--primary))",
  color: "hsl(var(--primary-foreground))",
};

const btnGhost = {
  ...btnBase,
  background: "transparent",
  color: "hsl(var(--muted-foreground))",
  borderColor: "hsl(var(--input))",
};

const inputStyle = {
  width: "100%",
  maxWidth: "100%",
  padding: "11px 13px",
  border: "1px solid hsl(var(--input))",
  borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
  background: "hsl(var(--background))",
  color: "hsl(var(--foreground))",
  fontSize: 14,
  outline: "none",
};

function Skeleton({ height = 14, width = "100%", radius = 8 }) {
  return <div style={{ height, width, borderRadius: radius, background: "rgba(0,0,0,0.07)" }} />;
}

function TerminalCard({ tone, icon, title, children, html }) {
  const bg =
    tone === "ok" ? "hsl(142 60% 40% / 0.15)" : tone === "error" ? "hsl(0 72% 50% / 0.12)" : tone === "info" ? "hsl(var(--primary) / 0.12)" : "hsl(var(--input))";
  const fg = tone === "ok" ? "hsl(142 70% 32%)" : tone === "error" ? "hsl(0 72% 42%)" : "hsl(var(--muted-foreground))";
  return (
    <div style={{ ...cardStyle, maxWidth: 460, margin: "24px auto", padding: "34px 24px", textAlign: "center" }}>
      <div style={{ width: 62, height: 62, borderRadius: 20, display: "grid", placeItems: "center", fontSize: 26, margin: "0 auto 16px", background: bg, color: fg }} aria-hidden>
        {icon}
      </div>
      {title ? <h1 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 600, overflowWrap: "anywhere" }}>{title}</h1> : null}
      {html ? (
        // Sanitised SERVER-side at save time. The browser never sees the client's raw markup.
        <div style={{ fontSize: 13.5, color: "hsl(var(--muted-foreground))", overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <div style={{ fontSize: 13.5, color: "hsl(var(--muted-foreground))", overflowWrap: "anywhere" }}>{children}</div>
      )}
      {html ? <div style={{ marginTop: 18 }}>{children}</div> : null}
    </div>
  );
}

/* ── Question rendering ──────────────────────────────────────────────────────────────────────────── */

function isBlock(q) {
  return q.type === "section_break" || q.type === "html_note";
}

function numberOf(questions, question) {
  let n = 0;
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i];
    if (isBlock(q)) continue;
    n += 1;
    if (q.key === question.key) return n;
  }
  return null;
}

function cfg(question, key, dflt) {
  const c = question.config || {};
  return c[key] === undefined || c[key] === null ? dflt : c[key];
}

function optionsOf(question) {
  const raw = cfg(question, "options", []);
  if (!Array.isArray(raw)) return [];
  return raw
    .map((o) => (typeof o === "string" ? { label: o, value: o } : { label: String(o.label || ""), value: String(o.value || o.label || "") }))
    .filter((o) => o.label !== "");
}

function QuestionBlock({ question, value, error, number, onChange }) {
  const id = `sv-${question.key}`;

  if (question.type === "section_break") {
    return (
      <div style={{ padding: "8px 0 2px" }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "hsl(var(--foreground))", overflowWrap: "anywhere" }}>{question.label}</h2>
        {cfg(question, "description", "") ? (
          <p style={{ margin: "4px 0 0", fontSize: 13.5, color: "hsl(var(--muted-foreground))", overflowWrap: "anywhere" }}>
            {cfg(question, "description", "")}
          </p>
        ) : null}
      </div>
    );
  }

  if (question.type === "html_note") {
    return (
      <div
        style={{
          borderLeft: "3px solid hsl(var(--primary))",
          background: "hsl(var(--primary) / 0.07)",
          borderRadius: "calc(var(--survey-radius, 12px) - 6px)",
          padding: "12px 14px",
          fontSize: 13.5,
          color: "hsl(var(--muted-foreground))",
          overflowWrap: "anywhere",
        }}
        // Sanitised on the server at save time.
        dangerouslySetInnerHTML={{ __html: cfg(question, "html", "") }}
      />
    );
  }

  // Hidden fields never render for a respondent.
  if (question.type === "hidden") return null;

  const describedBy = error ? `${id}-err` : cfg(question, "help", "") ? `${id}-help` : undefined;

  const label = (
    <label htmlFor={id} style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6, color: "hsl(var(--foreground))", overflowWrap: "anywhere" }}>
      {number != null && <span style={{ fontFamily: "monospace", fontSize: 12, color: "hsl(var(--muted-foreground))", marginRight: 6 }}>{number}.</span>}
      {question.label}
      {question.required ? <span style={{ color: "hsl(var(--destructive))", marginLeft: 4 }} aria-hidden>*</span> : null}
      {question.required ? <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}> (required)</span> : null}
    </label>
  );

  const help = cfg(question, "help", "") || question.help;

  return (
    <div style={{ ...cardStyle, borderColor: error ? "hsl(var(--destructive))" : "hsl(var(--border))" }}>
      {question.type !== "consent" && label}
      {question.type !== "consent" && help ? (
        <p id={`${id}-help`} style={{ margin: "0 0 10px", fontSize: 12.5, color: "hsl(var(--muted-foreground))", overflowWrap: "anywhere" }}>
          {help}
        </p>
      ) : null}

      <Control question={question} id={id} value={value} onChange={onChange} describedBy={describedBy} invalid={!!error} />

      {error ? (
        <p id={`${id}-err`} style={{ margin: "8px 0 0", fontSize: 12.5, fontWeight: 600, color: "hsl(var(--destructive))", display: "flex", gap: 6, alignItems: "flex-start" }}>
          <span aria-hidden>⚠</span>
          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

function Control({ question, id, value, onChange, describedBy, invalid }) {
  const common = {
    id,
    "aria-invalid": invalid || undefined,
    "aria-describedby": describedBy,
    "aria-required": question.required || undefined,
    style: inputStyle,
  };

  switch (question.type) {
    case "textarea":
      return (
        <textarea
          {...common}
          rows={cfg(question, "rows", 4)}
          placeholder={cfg(question, "placeholder", "")}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "number":
      return (
        <input
          {...common}
          type="number"
          inputMode="numeric"
          min={cfg(question, "min", undefined)}
          max={cfg(question, "max", undefined)}
          step={cfg(question, "step", 1)}
          placeholder={cfg(question, "placeholder", "")}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "date":
      return (
        <input
          {...common}
          type="date"
          min={cfg(question, "min", undefined) || undefined}
          max={cfg(question, "max", undefined) || undefined}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "select":
      return (
        <select {...common} value={value == null ? "" : String(value)} onChange={(e) => onChange(e.target.value)}>
          <option value="">{cfg(question, "placeholder", "Choose one…")}</option>
          {optionsOf(question).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );

    case "radio":
    case "checkbox": {
      const multi = question.type === "checkbox";
      const picked = multi ? (Array.isArray(value) ? value.map(String) : []) : value == null ? [] : [String(value)];
      const options = optionsOf(question);
      return (
        // A real fieldset/legend: this is the difference between a screen reader announcing the question
        // as a group and announcing nothing at all.
        <fieldset style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <legend style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>{question.label}</legend>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {options.map((o) => {
              const on = picked.indexOf(o.value) !== -1;
              return (
                <label
                  key={o.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    // 44px minimum target.
                    minHeight: 44,
                    padding: "10px 13px",
                    border: `1px solid ${on ? "hsl(var(--primary))" : "hsl(var(--input))"}`,
                    background: on ? "hsl(var(--primary) / 0.08)" : "hsl(var(--background))",
                    borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
                    fontSize: 14,
                    fontWeight: on ? 600 : 400,
                    cursor: "pointer",
                    color: "hsl(var(--foreground))",
                  }}
                >
                  <input
                    type={multi ? "checkbox" : "radio"}
                    name={id}
                    value={o.value}
                    checked={on}
                    aria-describedby={describedBy}
                    onChange={() => {
                      if (!multi) return onChange(o.value);
                      const next = picked.slice();
                      const at = next.indexOf(o.value);
                      if (at === -1) next.push(o.value);
                      else next.splice(at, 1);
                      onChange(next);
                    }}
                    style={{ width: 18, height: 18, flex: "0 0 auto", accentColor: "hsl(var(--primary))", margin: 0 }}
                  />
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{o.label}</span>
                </label>
              );
            })}
            {options.length === 0 && (
              <p style={{ margin: 0, fontSize: 12.5, fontStyle: "italic", color: "hsl(var(--muted-foreground))" }}>
                No options have been set for this question.
              </p>
            )}
          </div>
        </fieldset>
      );
    }

    case "consent": {
      const on = value === true;
      const url = cfg(question, "linkUrl", "");
      return (
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 11,
            padding: "12px 13px",
            border: `1px solid ${on ? "hsl(var(--primary))" : "hsl(var(--input))"}`,
            background: on ? "hsl(var(--primary) / 0.08)" : "hsl(var(--background))",
            borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
            fontSize: 14,
            cursor: "pointer",
            color: "hsl(var(--foreground))",
          }}
        >
          <input
            id={id}
            type="checkbox"
            checked={on}
            aria-describedby={describedBy}
            aria-required={question.required || undefined}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: 18, height: 18, flex: "0 0 auto", marginTop: 2, accentColor: "hsl(var(--primary))" }}
          />
          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {cfg(question, "statement", "I agree.")}
            {url ? (
              <>
                {" "}
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "hsl(var(--primary))" }}>
                  {cfg(question, "linkLabel", "Read the terms")}
                </a>
              </>
            ) : null}
            {question.required ? <span style={{ color: "hsl(var(--destructive))", marginLeft: 4 }}>*</span> : null}
          </span>
        </label>
      );
    }

    case "rating": {
      const max = Math.max(2, Math.min(10, Number(cfg(question, "max", 5)) || 5));
      const shape = cfg(question, "shape", "stars");
      const current = Number(value) || 0;
      const glyph = shape === "hearts" ? "♥" : "★";
      return (
        <div role="radiogroup" aria-label={question.label} aria-describedby={describedBy} style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
            const on = n <= current;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={n === current}
                aria-label={`${n} of ${max}`}
                onClick={() => onChange(n)}
                style={{
                  width: 44,
                  height: 44,
                  flex: "0 0 auto",
                  border: shape === "numbers" ? `1px solid ${on ? "hsl(var(--primary))" : "hsl(var(--input))"}` : "none",
                  background: shape === "numbers" && on ? "hsl(var(--primary))" : "transparent",
                  color: shape === "numbers" ? (on ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))") : on ? "#E8A33D" : "hsl(var(--input))",
                  borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
                  fontSize: shape === "numbers" ? 14 : 26,
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {shape === "numbers" ? n : glyph}
              </button>
            );
          })}
        </div>
      );
    }

    case "scale": {
      const min = Number(cfg(question, "min", 0));
      const max = Number(cfg(question, "max", 10));
      const count = Math.max(2, Math.min(21, max - min + 1));
      const minLabel = cfg(question, "minLabel", "");
      const maxLabel = cfg(question, "maxLabel", "");
      return (
        <div>
          {/*
            Scrolls horizontally rather than shrinking the targets below 44px. `WebkitOverflowScrolling`
            keeps it from feeling stuck on iOS.
          */}
          <div
            role="radiogroup"
            aria-label={question.label}
            aria-describedby={describedBy}
            style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}
          >
            {Array.from({ length: count }, (_, i) => min + i).map((n) => {
              const on = Number(value) === n && value !== undefined && value !== null && value !== "";
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => onChange(n)}
                  style={{
                    minWidth: 44,
                    height: 44,
                    flex: "0 0 auto",
                    border: `1px solid ${on ? "hsl(var(--primary))" : "hsl(var(--input))"}`,
                    background: on ? "hsl(var(--primary))" : "hsl(var(--background))",
                    color: on ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))",
                    fontWeight: on ? 700 : 400,
                    fontFamily: "monospace",
                    fontVariantNumeric: "tabular-nums",
                    borderRadius: "calc(var(--survey-radius, 12px) - 4px)",
                    fontSize: 14,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {n}
                </button>
              );
            })}
          </div>
          {minLabel || maxLabel ? (
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 6, fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>
              <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{minLabel}</span>
              <span style={{ minWidth: 0, overflowWrap: "anywhere", textAlign: "right" }}>{maxLabel}</span>
            </div>
          ) : null}
        </div>
      );
    }

    case "file":
      /**
       * Deliberately NOT a working upload yet.
       *
       * A public, unauthenticated upload endpoint needs its own storage cap, MIME allowlist and
       * anti-abuse gate — that is phase 8 of the build order, not something to improvise here. Saying so
       * plainly is better than shipping a picker that silently discards the file, which is exactly how a
       * client discovers at her event that the abstracts were never collected.
       */
      return (
        <div style={{ border: "1.5px dashed hsl(var(--input))", borderRadius: "calc(var(--survey-radius, 12px) - 4px)", padding: 16, textAlign: "center", fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
          File uploads are not switched on for this survey yet.
        </div>
      );

    case "email":
    case "phone":
    case "text":
    default:
      return (
        <input
          {...common}
          type={question.type === "email" ? "email" : question.type === "phone" ? "tel" : "text"}
          inputMode={question.type === "email" ? "email" : question.type === "phone" ? "tel" : undefined}
          autoComplete={question.type === "email" ? "email" : question.type === "phone" ? "tel" : "off"}
          placeholder={cfg(question, "placeholder", "")}
          value={value == null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
